import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calendar,
  ClipboardList,
  Download,
  FileText,
  FlaskConical,
  Layers,
  Loader2,
  MessageSquare,
  Paperclip,
  Pill,
} from "lucide-react";
import { usePatientAuth } from "../../store/authPatientStore";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import {
  getConsultationPdf,
  getPatientConsultations,
  getPatientPrescriptions,
  getPrescriptionPdf,
  type PatientConsultation,
} from "../../api/authPatient";
import { cn, downloadFileFromUrl } from "../../lib/utils";
import FileAttachmentLink, { type FileAttachmentData } from "../../components/ui/FileAttachment";
import {
  extractOrdonnanceEntityId,
  filterOrdonnanceRowsForConsultation,
} from "../../features/pro/ordonnancePharmacyHelpers";

// Alignés sur les onglets de la consultation côté professionnel (PatientDetail.tsx) :
// Observations / Analyses / Résultats / Ordonnances, en plus de Historique/Détail conservés ici.
type DetailTabId = "historique" | "detail" | "observations" | "analyses" | "resultats" | "ordonnances";

const DETAIL_TABS: { id: DetailTabId; label: string; icon: typeof Layers }[] = [
  { id: "historique", label: "Historique", icon: Calendar },
  { id: "detail", label: "Détail", icon: Layers },
  { id: "observations", label: "Observations", icon: MessageSquare },
  { id: "analyses", label: "Analyses", icon: FlaskConical },
  { id: "resultats", label: "Résultats", icon: FileText },
  { id: "ordonnances", label: "Ordonnances", icon: Pill },
];

function rowKey(c: PatientConsultation, index: number): string {
  if (c.id) return String(c.id);
  return `__row_${index}_${c.date ?? ""}_${c.motif ?? ""}`;
}

function formatDocumentEntry(doc: unknown, index: number): FileAttachmentData & { label: string } {
  if (typeof doc === "string") {
    return { label: doc || `Document ${index + 1}`, url: doc, fileName: doc || `Document ${index + 1}` };
  }
  if (doc && typeof doc === "object" && !Array.isArray(doc)) {
    const o = doc as Record<string, unknown>;
    const name = [o.fileName, o.nom, o.name, o.libelle, o.filename].find(
      (x) => typeof x === "string" && x,
    ) as string | undefined;
    const url = [o.bucketUrl, o.url, o.lien, o.href].find((x) => typeof x === "string" && x) as string | undefined;
    const mimeType = typeof o.mimeType === "string" ? o.mimeType : undefined;
    const label = name || `Document ${index + 1}`;
    return { label, url: url ?? "", fileName: label, mimeType };
  }
  return { label: `Document ${index + 1}`, url: "", fileName: `Document ${index + 1}` };
}

function parseComplement(raw: unknown): {
  analyses?: string[];
  resultats?: { disease?: string; note?: string };
  ordonnances?: Array<{ medicament?: string; note?: string }>;
} | null {
  let comp: unknown = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      comp = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!comp || typeof comp !== "object" || Array.isArray(comp)) return null;
  const o = comp as Record<string, unknown>;
  const analyses = Array.isArray(o.analyses)
    ? o.analyses.map((item) => String(item)).filter(Boolean)
    : undefined;
  const resultatsRaw =
    o.resultats && typeof o.resultats === "object" && !Array.isArray(o.resultats)
      ? (o.resultats as Record<string, unknown>)
      : undefined;
  const resultats = resultatsRaw
    ? {
        disease: typeof resultatsRaw.disease === "string" ? resultatsRaw.disease : undefined,
        note: typeof resultatsRaw.note === "string" ? resultatsRaw.note : undefined,
      }
    : undefined;
  const ordonnances = Array.isArray(o.ordonnances)
    ? o.ordonnances
        .filter((item) => item && typeof item === "object")
        .map((item) => {
          const row = item as Record<string, unknown>;
          return {
            medicament: typeof row.medicament === "string" ? row.medicament : undefined,
            note: typeof row.note === "string" ? row.note : undefined,
          };
        })
    : undefined;
  return { analyses, resultats, ordonnances };
}

export default function PatientConsultations() {
  const { patient, activeProfil } = usePatientAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [consultations, setConsultations] = useState<PatientConsultation[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [detailTab, setDetailTab] = useState<DetailTabId>("historique");
  const [pendingDeepLinkTab, setPendingDeepLinkTab] = useState<DetailTabId | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [ordonnanceId, setOrdonnanceId] = useState<string | null>(null);
  const [ordonnanceIdLoading, setOrdonnanceIdLoading] = useState(false);
  const [ordonnancePdfLoading, setOrdonnancePdfLoading] = useState(false);
  const [ordonnancePdfError, setOrdonnancePdfError] = useState<string | null>(null);

  const fullName = useMemo(
    () => (activeProfil ? `${activeProfil.nom} ${activeProfil.prenom}`.trim() : `${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim()),
    [activeProfil, patient]
  );
  const patientNip = activeProfil?.nip?.trim() || patient?.nip?.trim() || "";

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!patientNip) {
        if (!mounted) return;
        setConsultations([]);
        setSelectedId("");
        setError("NIP patient introuvable. Impossible de charger les consultations.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const result = await getPatientConsultations(patientNip);
      if (!mounted) return;

      if (!result.ok) {
        setConsultations([]);
        setSelectedId("");
        setError(result.message || "Impossible de charger les consultations.");
        setLoading(false);
        return;
      }

      setConsultations(result.data);
      setSelectedId("");
      setLoading(false);
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [patientNip]);

  const selectedConsultation = useMemo(() => {
    if (!selectedId) return null;
    const idx = consultations.findIndex((c, i) => rowKey(c, i) === selectedId);
    if (idx < 0) return null;
    return consultations[idx] ?? null;
  }, [consultations, selectedId]);

  useEffect(() => {
    if (selectedId) {
      setDetailTab(pendingDeepLinkTab ?? "historique");
      setPendingDeepLinkTab(null);
    }
  }, [selectedId, pendingDeepLinkTab]);

  // Lien profond depuis /patient/ordonnances ("Voir la consultation") : ?consultationId=... sélectionne
  // directement la bonne consultation et ouvre son onglet Ordonnances.
  useEffect(() => {
    if (loading || consultations.length === 0) return;
    const targetId = searchParams.get("consultationId");
    if (!targetId) return;
    if (consultations.some((c) => c.id === targetId)) {
      setPendingDeepLinkTab("ordonnances");
      setSelectedId(targetId);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("consultationId");
        return next;
      },
      { replace: true },
    );
  }, [loading, consultations, searchParams, setSearchParams]);

  // Retrouve l'ordonnance (réelle, en base) liée à la consultation sélectionnée — nécessaire pour
  // télécharger son PDF, distinct des lignes medicament/note stockées dans `complement.ordonnances`.
  useEffect(() => {
    let cancelled = false;
    setOrdonnanceId(null);
    setOrdonnancePdfError(null);
    const consultationId = selectedConsultation?.id;
    const statut = (selectedConsultation?.statutConsultation ?? "").toLowerCase();
    if (!consultationId || !patientNip || statut === "ouverte") {
      setOrdonnanceIdLoading(false);
      return;
    }
    setOrdonnanceIdLoading(true);
    void (async () => {
      const res = await getPatientPrescriptions(patientNip);
      if (cancelled) return;
      setOrdonnanceIdLoading(false);
      if (!res.ok) return;
      const matched = filterOrdonnanceRowsForConsultation(res.data, consultationId);
      const id = matched.length > 0 ? extractOrdonnanceEntityId(matched[0] as Record<string, unknown>) : "";
      setOrdonnanceId(id || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedConsultation?.id, selectedConsultation?.statutConsultation, patientNip]);

  const handleDownloadOrdonnancePdf = async () => {
    if (!ordonnanceId) return;
    setOrdonnancePdfLoading(true);
    setOrdonnancePdfError(null);
    const res = await getPrescriptionPdf(ordonnanceId);
    setOrdonnancePdfLoading(false);
    if (!res.ok) {
      setOrdonnancePdfError(res.message);
      return;
    }
    await downloadFileFromUrl(res.pdfUrl, `ordonnance-${ordonnanceId}.pdf`);
  };

  const formatDate = (value?: string) => {
    if (!value) return "Date non renseignée";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(parsed);
  };

  const handleDownloadPdf = async (consultationId: string) => {
    if (!consultationId) {
      setPdfError("Identifiant de consultation manquant.");
      return;
    }
    setPdfLoading(true);
    setPdfError(null);
    const res = await getConsultationPdf(consultationId);
    setPdfLoading(false);
    if (!res.ok) {
      setPdfError(res.message);
      return;
    }
    await downloadFileFromUrl(res.pdfUrl, `consultation-${consultationId}.pdf`);
  };

  const documentsList = useMemo(() => {
    const raw = selectedConsultation?.documents;
    if (!Array.isArray(raw)) return [];
    return raw.map((doc, i) => formatDocumentEntry(doc, i));
  }, [selectedConsultation?.documents]);
  const selectedComplement = useMemo(
    () => parseComplement(selectedConsultation?.complement),
    [selectedConsultation?.complement]
  );

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Consultations"
        description={fullName ? `Historique des consultations de ${fullName}` : "Historique de vos consultations"}
      />

      <div className="rounded-[12px] border bg-white p-4 md:p-6" style={{ borderColor: "#E5E7EB" }}>
        {loading && <p className="text-sm text-gray-500">Chargement des consultations…</p>}

        {!loading && error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{error}</div>
        )}

        {!loading && !error && consultations.length === 0 && (
          <div className="text-sm text-gray-600 bg-gray-50 border rounded-[10px] px-3 py-2" style={{ borderColor: "#E5E7EB" }}>
            Aucune consultation disponible pour le moment.
          </div>
        )}

        {!loading && !error && consultations.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6 lg:items-start">
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="flex items-center gap-2 text-[#0B1F16] mb-3">
                <ClipboardList className="size-5 shrink-0 text-[#00553E]" aria-hidden />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Liste des consultations</h2>
              </div>
              <div className="space-y-2 max-h-[min(520px,70vh)] overflow-y-auto pr-1">
                {consultations.map((consultation, index) => {
                  const key = rowKey(consultation, index);
                  const active = key === selectedId;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedId(key)}
                      className={cn(
                        "w-full text-left rounded-[10px] border px-4 py-3 transition-all",
                        active
                          ? "bg-[#E8F4EF] border-[#00553E] shadow-sm ring-1 ring-[#00553E]/20"
                          : "bg-white border-[#E5E7EB] hover:border-[#00553E]/40 hover:bg-[#FAFBFB]",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-[#0B1F16] line-clamp-2">
                          {consultation.typeConsultation || "Consultation"}
                        </p>
                        {consultation.statutConsultation && (
                          <span
                            className={cn(
                              "shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold",
                              consultation.statutConsultation === "fermee"
                                ? "bg-[#DCEFE9] text-[#00553E]"
                                : consultation.statutConsultation === "ouverte"
                                  ? "bg-amber-100 text-amber-900"
                                  : "bg-red-100 text-red-700",
                            )}
                          >
                            {consultation.statutConsultation}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{consultation.motif || "Motif non renseigné"}</p>
                      <p className="mt-2 text-xs text-gray-500">{formatDate(consultation.date)}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-7 xl:col-span-8 min-h-[min(420px,60vh)]">
              {!selectedConsultation ? (
                <div
                  className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-[12px] border border-dashed px-6 py-12 text-center"
                  style={{ borderColor: "#D1E2DD", background: "#FAFBFB" }}
                >
                  <ClipboardList className="size-12 text-[#00553E]/40 mb-3" aria-hidden />
                  <p className="text-sm font-semibold text-[#0B1F16]">Sélectionnez une consultation</p>
                  <p className="mt-1 max-w-sm text-sm text-gray-500">
                    Cliquez sur une entrée de la liste pour afficher le détail, les observations, analyses, résultats et ordonnances.
                  </p>
                </div>
              ) : (
                <div
                  className="flex h-full min-h-[360px] flex-col overflow-hidden rounded-[12px] border bg-white shadow-sm"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <div
                    className="shrink-0 border-b px-4 py-3 md:px-5"
                    style={{ borderColor: "#E5E7EB", background: "linear-gradient(180deg, #F8FAFA 0%, #FFFFFF 100%)" }}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#00553E]">Consultation sélectionnée</p>
                    <p className="mt-1 text-base font-semibold text-[#0B1F16] leading-snug">
                      {selectedConsultation.typeConsultation || "Consultation"}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-600">{formatDate(selectedConsultation.date)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      {selectedConsultation.statutConsultation && (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 font-semibold",
                            selectedConsultation.statutConsultation === "fermee"
                              ? "bg-[#DCEFE9] text-[#00553E]"
                              : selectedConsultation.statutConsultation === "ouverte"
                                ? "bg-amber-100 text-amber-900"
                                : "bg-red-100 text-red-700",
                          )}
                        >
                          {selectedConsultation.statutConsultation}
                        </span>
                      )}
                      {selectedConsultation.etatSortie && (
                        <span className="inline-flex items-center rounded-full bg-[#EEF1F1] px-2.5 py-1 font-semibold text-[#0B1F16]">
                          Sortie: {selectedConsultation.etatSortie}
                        </span>
                      )}
                      {selectedConsultation.dateCloture && (
                        <span className="inline-flex items-center rounded-full bg-[#EEF1F1] px-2.5 py-1 font-semibold text-[#0B1F16]">
                          Clôturée: {formatDate(selectedConsultation.dateCloture)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 border-b overflow-x-auto" style={{ borderColor: "#E5E7EB" }}>
                    <nav className="flex min-w-0 gap-0.5 px-2 py-2 md:px-3" aria-label="Sections de la consultation">
                      {DETAIL_TABS.map(({ id, label, icon: Icon }) => {
                        const isActive = detailTab === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setDetailTab(id)}
                            className={cn(
                              "flex shrink-0 items-center gap-1.5 rounded-[8px] px-3 py-2 text-xs font-medium transition-colors md:text-sm",
                              isActive
                                ? "bg-[#DCEFE9] text-[#00553E] shadow-sm"
                                : "text-gray-600 hover:bg-gray-50 hover:text-[#0B1F16]",
                            )}
                          >
                            <Icon className="size-3.5 shrink-0 opacity-80 md:size-4" aria-hidden />
                            {label}
                          </button>
                        );
                      })}
                    </nav>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
                    {detailTab === "historique" && (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">Vue d’ensemble de cette consultation dans votre parcours de soins.</p>
                        <div className="relative pl-4 border-l-2 border-[#00553E]/30">
                          <div className="absolute -left-[5px] top-1.5 size-2 rounded-full bg-[#00553E]" aria-hidden />
                          <p className="text-xs font-medium uppercase text-gray-500">Événement</p>
                          <p className="mt-1 text-sm font-semibold text-[#0B1F16]">
                            {selectedConsultation.typeConsultation || "Consultation"}
                          </p>
                          <p className="text-sm text-gray-600">{formatDate(selectedConsultation.date)}</p>
                          {selectedConsultation.motif && (
                            <p className="mt-2 text-sm text-gray-700 line-clamp-3">{selectedConsultation.motif}</p>
                          )}
                          {selectedComplement?.analyses && selectedComplement.analyses.length > 0 && (
                            <p className="mt-2 text-xs text-gray-600">
                              Analyses: {selectedComplement.analyses.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {detailTab === "detail" && (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Type de consultation</div>
                          <div className="mt-1 font-semibold text-[#0B1F16]">
                            {selectedConsultation.typeConsultation || "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Date</div>
                          <div className="mt-1 font-semibold text-[#0B1F16]">{formatDate(selectedConsultation.date)}</div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Professionnel (NIP)</div>
                          <div className="mt-1 font-semibold text-[#0B1F16] break-all">
                            {selectedConsultation.professionnelNip || "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Établissement</div>
                          <div className="mt-1 font-semibold text-[#0B1F16] break-all">
                            {selectedConsultation.etablissementId || "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Statut consultation</div>
                          <div className="mt-1 font-semibold text-[#0B1F16] break-all">
                            {selectedConsultation.statutConsultation || "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">État de sortie</div>
                          <div className="mt-1 font-semibold text-[#0B1F16] break-all">
                            {selectedConsultation.etatSortie || "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Date de clôture</div>
                          <div className="mt-1 font-semibold text-[#0B1F16] break-all">
                            {selectedConsultation.dateCloture ? formatDate(selectedConsultation.dateCloture) : "Non renseigné"}
                          </div>
                        </div>
                        <div className="rounded-[10px] border bg-[#FAFBFB] p-3 sm:col-span-2" style={{ borderColor: "#E5E7EB" }}>
                          <div className="text-xs font-medium text-gray-500">Motif</div>
                          <div className="mt-1 font-semibold text-[#0B1F16]">
                            {selectedConsultation.motif || "Non renseigné"}
                          </div>
                        </div>
                      </div>
                    )}

                    {detailTab === "observations" && (
                      <p className="text-sm leading-relaxed text-[#334155] whitespace-pre-wrap">
                        {selectedConsultation.observations || "Aucune observation disponible pour cette consultation."}
                      </p>
                    )}

                    {detailTab === "analyses" && (
                      <div className="space-y-5">
                        <div>
                          <div className="font-semibold text-[#0B1F16] mb-2">Analyses effectuées</div>
                          {selectedComplement?.analyses && selectedComplement.analyses.length > 0 ? (
                            <ul className="space-y-1.5 rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
                              {selectedComplement.analyses.map((name, i) => (
                                <li
                                  key={i}
                                  className="rounded-[6px] bg-white px-3 py-2 text-sm text-[#0B1F16]"
                                  style={{ border: "1px solid rgba(0,0,0,0.06)" }}
                                >
                                  {name}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-600">Aucune analyse enregistrée pour cette consultation.</p>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0B1F16] mb-2">Pièces jointes</div>
                          {documentsList.length === 0 ? (
                            <p className="text-sm text-gray-600">Aucun document joint pour cette consultation.</p>
                          ) : (
                            <ul className="space-y-2">
                              {documentsList.map((doc, i) => (
                                <li key={`${doc.label}-${i}`}>
                                  {doc.url ? (
                                    <FileAttachmentLink file={doc} />
                                  ) : (
                                    <span className="flex items-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-[#FAFBFB] px-3 py-2.5 text-sm font-medium text-[#0B1F16]">
                                      <Paperclip className="size-4 shrink-0 text-[#00553E]" aria-hidden />
                                      {doc.label}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}

                    {detailTab === "resultats" && (
                      <div>
                        <div className="font-semibold text-[#0B1F16] mb-2">Diagnostic / Résultats</div>
                        {selectedComplement?.resultats ? (
                          <>
                            <div className="rounded-[10px] px-4 py-3 font-medium bg-[#DAF1E9] text-[#0B1F16]">
                              {selectedComplement.resultats.disease || "Non renseigné"}
                            </div>
                            <div
                              className="mt-4 rounded-[10px] border bg-[#FAFBFB] p-4"
                              style={{ borderColor: "#E5E7EB" }}
                            >
                              <p className="whitespace-pre-wrap text-sm text-[#334155]">
                                {selectedComplement.resultats.note || "—"}
                              </p>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-600">Aucun résultat enregistré pour cette consultation.</p>
                        )}
                      </div>
                    )}

                    {detailTab === "ordonnances" && (
                      <div>
                        {selectedComplement?.ordonnances && selectedComplement.ordonnances.length > 0 ? (
                          <>
                            <div className="grid grid-cols-2 gap-4 mb-1 px-1">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Médicament</span>
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Posologie</span>
                            </div>
                            <div className="rounded-[10px] overflow-hidden border" style={{ borderColor: "#E5E7EB" }}>
                              {selectedComplement.ordonnances.map((row, i) => (
                                <div
                                  key={i}
                                  className="grid grid-cols-2 border-b last:border-b-0 bg-[#FAFBFB]"
                                  style={{ borderColor: "#E5E7EB" }}
                                >
                                  <div className="p-3 font-medium text-[#0B1F16]">{row.medicament || "—"}</div>
                                  <div className="p-3 text-[#334155]">{row.note || "—"}</div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-600">Aucune ordonnance pour cette consultation.</p>
                        )}
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => void handleDownloadOrdonnancePdf()}
                            disabled={ordonnancePdfLoading || ordonnanceIdLoading || !ordonnanceId}
                            title={
                              !ordonnanceId && !ordonnanceIdLoading
                                ? "Aucune ordonnance enregistrée pour cette consultation"
                                : undefined
                            }
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-[6px] text-sm font-semibold text-white disabled:opacity-50"
                            style={{ background: "#00553E" }}
                          >
                            {ordonnancePdfLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Download size={14} />
                            )}
                            {ordonnancePdfLoading ? "Génération…" : "Télécharger l'ordonnance"}
                          </button>
                          {ordonnancePdfError && (
                            <p className="mt-2 text-xs text-red-600">{ordonnancePdfError}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t px-4 py-3 md:px-5 space-y-2" style={{ borderColor: "#E5E7EB", background: "#FAFBFB" }}>
                    {pdfError && (
                      <p className="text-xs text-red-600">{pdfError}</p>
                    )}
                    <button
                      type="button"
                      disabled={pdfLoading || !selectedConsultation.id}
                      onClick={() => void handleDownloadPdf(selectedConsultation.id!)}
                      className="inline-flex items-center gap-2 w-full md:w-auto h-10 md:h-11 px-5 rounded-[10px] text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
                      style={{ background: "#00553E" }}
                    >
                      {pdfLoading
                        ? <><Loader2 size={16} className="animate-spin" /> Génération du PDF…</>
                        : <><Download size={16} /> Télécharger le dossier complet (PDF)</>
                      }
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
